// src/components/ui/error-display.tsx
import React from 'react';
import { AlertTriangle, AlertCircle, Copy, RefreshCw, FileQuestion } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './card';
import { cn } from '@/lib';

interface ErrorDisplayProps {
  title: string;
  message: string;
  details?: string;
  suggestion?: string;
  variant?: 'error' | 'warning' | 'info';
  className?: string;
  onRetry?: () => void;
  onCopy?: () => void;
  onClose?: () => void;
}

export function ErrorDisplay({
  title,
  message,
  details,
  suggestion,
  variant = 'error',
  className,
  onRetry,
  onCopy,
  onClose,
}: ErrorDisplayProps) {
  const variantStyles = {
    error: {
      icon: <AlertCircle className="h-6 w-6 text-destructive" />,
      background: 'bg-destructive/10',
      border: 'border-destructive/30',
    },
    warning: {
      icon: <AlertTriangle className="h-6 w-6 text-amber-500" />,
      background: 'bg-amber-500/10',
      border: 'border-amber-500/30',
    },
    info: {
      icon: <FileQuestion className="h-6 w-6 text-blue-500" />,
      background: 'bg-blue-500/10',
      border: 'border-blue-500/30',
    },
  };

  const handleCopy = () => {
    if (onCopy) {
      onCopy();
      return;
    }
    
    // Default copy behavior
    const copyText = `${title}\n\n${message}${details ? '\n\nDetails:\n' + details : ''}`;
    navigator.clipboard.writeText(copyText)
      .catch(err => console.error('Could not copy text: ', err));
  };

  return (
    <Card className={cn("w-full", variantStyles[variant].background, variantStyles[variant].border, className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {variantStyles[variant].icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        <p className="text-sm mb-2">{message}</p>
        {details && (
          <div className="mt-2 p-3 bg-muted/50 rounded text-xs font-mono text-muted-foreground overflow-auto max-h-[200px]">
            {details}
          </div>
        )}
        {suggestion && (
          <p className="mt-2 text-xs text-muted-foreground">{suggestion}</p>
        )}
      </CardContent>
      <CardFooter className="flex justify-end gap-2 pt-0">
        {onCopy && (
          <Button variant="outline" size="sm" onClick={handleCopy} className="h-8 gap-1">
            <Copy className="h-3.5 w-3.5" />
            <span>Copy</span>
          </Button>
        )}
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="h-8 gap-1">
            <RefreshCw className="h-3.5 w-3.5" />
            <span>Retry</span>
          </Button>
        )}
        {onClose && (
          <Button variant="outline" size="sm" onClick={onClose} className="h-8">
            Close
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

interface QueryErrorDisplayProps {
  error: Error;
  query: string;
  tableSchemas: {
    tableName: string;
    columns: Array<{ name: string; type: string }>;
  }[];
  onCopy: () => void;
  onDismiss: () => void;
}

export function QueryErrorDisplay({
  error,
  query,
  tableSchemas,
  onCopy,
  onDismiss,
}: QueryErrorDisplayProps) {
  const errorMessage = error.message;
  
  // Extract the most relevant part of the error message
  const simplifiedError = errorMessage
    .replace(/Error: Binder Error: /g, '')
    .replace(/Error: Catalog Error: /g, '')
    .replace(/Error: Parser Error: /g, '')
    .replace(/Error: Syntax Error: /g, '')
    .replace(/Error: Conversion Error: /g, '')
    .replace(/Error: /g, '');
  
  // Format the schema information
  const schemasFormatted = tableSchemas.map(schema => {
    return `Table: ${schema.tableName}\nColumns:\n${schema.columns.map(col => 
      `  - ${col.name}: ${col.type}`
    ).join('\n')}`;
  }).join('\n\n');
  
  // Format the details for display and copying
  const details = `QUERY:\n${query}\n\nERROR:\n${simplifiedError}\n\nTABLE SCHEMAS:\n${schemasFormatted}`;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(details)
      .then(() => {
        if (onCopy) onCopy();
      })
      .catch(err => console.error('Could not copy error details: ', err));
  };
  
  return (
    <ErrorDisplay
      title="Query Execution Error"
      message={simplifiedError}
      details={details}
      suggestion="Check your query syntax and table/column names."
      variant="error"
      onCopy={handleCopy}
      onClose={onDismiss}
    />
  );
}

interface DataSourceErrorProps {
  title: string;
  message: string;
  source: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}

export function DataSourceError({
  title,
  message,
  source,
  onRetry,
  onDismiss,
}: DataSourceErrorProps) {
  const suggestions = {
    'file': 'Make sure the file exists, is in a supported format (CSV or Parquet), and is not corrupted.',
    'url': 'Check if the URL is correct and accessible. The server might be down or the file might be moved.',
    'database': 'Ensure the database connection is properly configured and the database is running.',
    'memory': 'The operation might require more memory than available. Try with a smaller dataset or restart the application.',
    'format': 'The data format appears to be incorrect. Check that the file matches the expected format (CSV, Parquet).',
    'permission': 'You might not have permission to access this resource. Check your credentials.',
    'network': 'There was a network error. Check your internet connection and try again.',
    'unknown': 'An unexpected error occurred. Try refreshing the page or restart the application.',
  }[source] || 'Please try again or use a different data source.';

  return (
    <ErrorDisplay
      title={title}
      message={message}
      suggestion={suggestions}
      variant="warning"
      onRetry={onRetry}
      onClose={onDismiss}
    />
  );
}